import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid } from "@/comn/components";
import { useForm, useFetch, useTheme, useStore, useToast, useModal, useResource, useGrid, usePage } from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_CSTM_OFCR_INFO,
    SG_CSTM_USR_ROLE_LIST,
    SF_ROLE_FILE_FORM,
} from "./services/PtliUsrRoleService";
import { Link } from "react-router-dom";
import { PTLI1030003Q } from "@/tra/tancis/ptli/ptl/usrrole/PTLI1030003Q";

/**
 * Internal role Details
 * !==  내부 사용자 역할 상세 ==!
 *
 */
export const PTLI1030002S = (props: any) => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const param = useParams();
    const { theme } = useTheme();
    const fileUuid = "";

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        cstmOfcrInfo: useForm({ defaultSchema: SF_CSTM_OFCR_INFO }),
        form_roleFileForm: useForm({ defaultSchema: SF_ROLE_FILE_FORM }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        cstmUsrRoleList: useGrid({ defaultSchema: SG_CSTM_USR_ROLE_LIST }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getCstmUsrRoleList: useFetch({
            api: (data) => APIS.getCstmUsrInfo(data),
            onSuccess: (data: any) => {
                const { content } = data.usrInfo;
                form.cstmOfcrInfo.setValues(content);
            },
            onError: () => modal.openModal({ content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        saveCstmUsrRoleList: useFetch({
            api: () =>
                APIS.saveCstmUsrRoleList({
                    usrDmnId: param["usrDmnId"],
                    cstmUsrRoleList: grid.cstmUsrRoleList.getData(),
                }),
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00062" }); // 정상적으로 저장되었습니다.
                fetch.getCstmUsrRoleList.fetch({ usrDmnId: param["usrDmnId"] });
            },
            onError: () => modal.openModal({ content: "msg.com.00026" }), // Failed to save. !==  저장이 실패하였습니다. ==!
        }),
    };

    /*
     * Render functions for grid cells
     * !== 그리드 셀 렌더링 함수 ==!
     */
    const render = {
        cell: {
            attchFle: (props: any) => {
                const rowData = props.rowValues;
                const isNewRow = rowData?.__type === "added";

                console.log("rowData", rowData);

                // get bsopId from fileSaveReqDtoList
                const bsopId = rowData.fileSaveDto?.fileSaveReqDtoList?.[0]?.bsopId || rowData.roleId;

                // set the form value
                form.form_roleFileForm.setValues({ fileGrpUuid: bsopId });

                const fileGrpUuid = form.form_roleFileForm.getValues("fileGrpUuid");
                console.log("fileGrpUuid", fileGrpUuid);

                if (isNewRow) {
                    // For new rows, show file upload control
                    return (
                        <Group.Control
                            {...form.form_roleFileForm.schema.fileGrpUuid}
                            onChange={(fileSaveDto: any) => {
                                const updatedRow = {
                                    ...rowData,
                                    roleMemoFileUuid: fileSaveDto.fileGrpUuid,
                                    fileSaveDto,
                                };

                                const rowIndex = grid.cstmUsrRoleList.getData().findIndex((r: any) => r === rowData);
                                grid.cstmUsrRoleList.updateRow(updatedRow);
                            }}
                        />
                    );
                } else {
                    // For existing rows, show file input or placeholder
                    return rowData.roleMemoFileUuid ? (
                        <Group.Control type="file" value={bsopId} edit={false} />
                    ) : (
                        <p>No file</p>
                    );
                }
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onBtnSaveClick: () => {
            const list = grid.cstmUsrRoleList.getData({ excludes: ["origin"] });
            console.log(list);
            if (list.length === 0) {
                modal.openModal({ content: "msg.ptl.00741" }); // There is no data to be stored and deleted. !== 저장 및 삭제 대상 데이터가 없습니다. ==!
                return;
            }

            list.forEach((item: any) => {
                if (item.fileGrpUuid && typeof item.fileGrpUuid === "object") {
                    item.fileSaveDto = item.fileGrpUuid; // Attach the FileSaveDto
                    item.roleMemoFileUuid = null; // Clear the old UUID field
                    item.fileGrpUuid = null; // Clear temporary field
                }
            });

            modal.openModal({
                content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                onConfirm: () => fetch.saveCstmUsrRoleList.fetch(),
            });
        },
        onBtnGrdAddRowClick: () => {
            const data = grid.cstmUsrRoleList.getData({ excludes: ["origin"] }); // INSERT, DELETE

            if (!comnUtils.isEmpty(data)) {
                modal.openModal({ content: "msg.ptl.00745" });
                return;
            }

            const modalCallback = (list: any) => {
                const roleList = grid.cstmUsrRoleList.getData();
                list.forEach((item: any) => {
                    const exists = roleList.findIndex((subItem: any) => item.roleId === subItem.roleId) !== -1;
                    if (!exists) {
                        grid.cstmUsrRoleList.addRow({
                            ...item,
                            __type: "added",
                            fileSaveDto: null,
                            roleMemoFileUuid: null,
                        });
                    }
                });
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "md",
                content: (
                    <PTLI1030003Q
                        usrDmnId={param["usrDmnId"]}
                        cstmUsrRoleList={grid.cstmUsrRoleList.getData()}
                        callback={modalCallback}
                    />
                ),
            });
        },
        onBtnGrdDelRowClick: () => {
            if (comnUtils.isEmpty(grid.cstmUsrRoleList.getChecked())) {
                modal.openModal({ content: "msg.com.00086" }); // No data has been selected.. !== 선택된 데이터가 없습니다. ==!
                return;
            }
            modal.openModal({
                content: "msg.com.00049",
                onConfirm: () => {
                    grid.cstmUsrRoleList.deleteRow("checkbox");
                },
            });
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getCstmUsrRoleList.fetch({ usrDmnId: param["usrDmnId"] });
    }, [theme]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrInfo.schema.sctnNm} />
                            <Group.Control {...form.cstmOfcrInfo.schema.cstmNm} />
                            <Group.Control {...form.cstmOfcrInfo.schema.unitNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrInfo.schema.usrDmnId} />
                            <Group.Control {...form.cstmOfcrInfo.schema.cstmOfcrNo} />
                            <Group.Control {...form.cstmOfcrInfo.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrInfo.schema.usrTelno} controlSize={2} />
                            <Group.Control {...form.cstmOfcrInfo.schema.usrEmlAddr} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Section gap={2}>
                        <Layout.Right>
                            <Button role={"gridAdd"} onClick={handler.onBtnGrdAddRowClick} />
                            <Button role={"gridDelete"} onClick={handler.onBtnGrdDelRowClick} />
                        </Layout.Right>

                        <Grid
                            {...grid.cstmUsrRoleList.grid}
                            data={fetch.getCstmUsrRoleList.data?.cstmUsrRoleList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout>
                <Layout.Left>
                    <Button role={"list"} onClick={() => navigate(`${URLS.ptli1030001q}`)} />
                </Layout.Left>
                <Layout.Right>
                    <Button role={"save"} onClick={handler.onBtnSaveClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
